// components/PromotionManagement.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import {
  ArrowUpward,
  Groups,
  SchoolOutlined,
  HistoryEdu,
  CheckCircle,
  Cancel,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";

const PromotionManagement = () => {
  const navigate = useNavigate();
  const { schoolId, schoolName } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStream, setSelectedStream] = useState("");
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [learners, setLearners] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [promotionStep, setPromotionStep] = useState(0);
  const [promotionDialog, setPromotionDialog] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promotionResult, setPromotionResult] = useState(null);
  const [schoolStreams, setSchoolStreams] = useState({});

  // Fetch classes and streams
  useEffect(() => {
    fetchClassesAndStreams();
  }, [schoolId]);

  const fetchClassesAndStreams = async () => {
    try {
      setLoading(true);
      const [learnersResponse, streamsResponse] = await Promise.all([
        axios.get("https://kidsm.vercel.app/api/schools/learners", {
          params: { schoolId }
        }),
        axios.get(`https://kidsm.vercel.app/api/schools/${schoolId}/streams`)
      ]);

      // Extract unique classes
      const uniqueClasses = [...new Set(learnersResponse.data.map(l => l.class))];
      setClasses(uniqueClasses.sort());
      
      if (streamsResponse.data.classStreams) {
        setSchoolStreams(streamsResponse.data.classStreams);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch learners for selected class
  const fetchLearnersForClass = async () => {
    if (!selectedClass) return;

    try {
      setLoading(true);
      const response = await axios.get("https://kidsm.vercel.app/api/schools/promotion-eligible", {
        params: {
          className: selectedClass,
          stream: selectedStream || undefined,
          academicYear: academicYear
        }
      });

      if (response.data.success) {
        setLearners(response.data.data.learners);
        setSelectedLearners([]);
      }
    } catch (error) {
      console.error("Error fetching learners:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClass) {
      fetchLearnersForClass();
    }
  }, [selectedClass, selectedStream, academicYear]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLearners(learners.map(l => l._id));
    } else {
      setSelectedLearners([]);
    }
  };

  const handleSelectLearner = (learnerId) => {
    if (selectedLearners.includes(learnerId)) {
      setSelectedLearners(selectedLearners.filter(id => id !== learnerId));
    } else {
      setSelectedLearners([...selectedLearners, learnerId]);
    }
  };

  const getNextClass = (currentClass) => {
    const classProgression = [
      "PP1", "PP2", 
      "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", 
      "Grade 6", "Grade 7", "Grade 8", "Grade 9", 
      "Grade 10", "Grade 11", "Grade 12"
    ];
    
    const currentIndex = classProgression.indexOf(currentClass);
    if (currentIndex === -1 || currentIndex === classProgression.length - 1) {
      return null;
    }
    
    return classProgression[currentIndex + 1];
  };

  const shouldGraduateToAlumni = (currentClass) => {
    return currentClass === "Grade 9" || currentClass === "Grade 12";
  };

  const handlePromote = async () => {
    if (selectedLearners.length === 0) {
      alert("Please select at least one learner to promote");
      return;
    }

    setPromoting(true);
    try {
      let response;
      const isGraduatingClass = shouldGraduateToAlumni(selectedClass);
      const nextClass = getNextClass(selectedClass);

      if (isGraduatingClass) {
        // Graduate to alumni
        response = await axios.post(
          "https://kidsm.vercel.app/api/schools/graduate-class-to-alumni",
          {
            className: selectedClass,
            stream: selectedStream || undefined,
            graduationYear: academicYear,
            skipLearners: learners
              .filter(l => !selectedLearners.includes(l._id))
              .map(l => l._id)
          }
        );
      } else {
        // Regular promotion
        response = await axios.post(
          "https://kidsm.vercel.app/api/schools/promote-learners-by-class",
          {
            className: selectedClass,
            stream: selectedStream || undefined,
            academicYear: academicYear,
            remarks: remarks,
            skipLearners: learners
              .filter(l => !selectedLearners.includes(l._id))
              .map(l => l._id)
          }
        );
      }

      if (response.data.success) {
        setPromotionResult(response.data);
        setPromotionStep(2);
        
        // Refresh data
        fetchLearnersForClass();
      }
    } catch (error) {
      console.error("Error promoting learners:", error);
      alert(error.response?.data?.message || "Failed to promote learners");
    } finally {
      setPromoting(false);
    }
  };

  const renderPromotionStep = () => {
    switch (promotionStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Learners to Promote
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {selectedLearners.length} of {learners.length} learners selected
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedLearners.length > 0 && selectedLearners.length < learners.length}
                        checked={learners.length > 0 && selectedLearners.length === learners.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Admission No.</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Stream</TableCell>
                    <TableCell>Parent Contact</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {learners.map((learner) => (
                    <TableRow key={learner._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLearners.includes(learner._id)}
                          onChange={() => handleSelectLearner(learner._id)}
                        />
                      </TableCell>
                      <TableCell>{learner.admissionNumber}</TableCell>
                      <TableCell>{learner.fullName}</TableCell>
                      <TableCell>{learner.stream || 'N/A'}</TableCell>
                      <TableCell>{learner.parentContact}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      
      case 1:
        const isGraduatingClass = shouldGraduateToAlumni(selectedClass);
        const nextClass = getNextClass(selectedClass);
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Promotion Details
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Current Class
                    </Typography>
                    <Typography variant="h5">
                      {selectedClass}
                      {selectedStream && ` (${selectedStream})`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Action
                    </Typography>
                    <Typography variant="h5">
                      {isGraduatingClass 
                        ? `Graduate to Alumni`
                        : `Promote to ${nextClass}`
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              {isGraduatingClass
                ? `${selectedLearners.length} learners will be graduated to Alumni from ${selectedClass}.`
                : `${selectedLearners.length} learners will be promoted from ${selectedClass} to ${nextClass}.`
              }
            </Alert>
            
            <Alert severity="warning">
              This action cannot be undone. Please review carefully before confirming.
            </Alert>
          </Box>
        );
      
      case 2:
        if (!promotionResult) return null;
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Promotion Results
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {promotionResult.summary.promoted || promotionResult.summary.graduated}
                    </Typography>
                    <Typography align="center">
                      Successfully {shouldGraduateToAlumni(selectedClass) ? 'Graduated' : 'Promoted'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#FF9800', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {promotionResult.summary.skipped}
                    </Typography>
                    <Typography align="center">
                      Skipped
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#F44336', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {promotionResult.summary.errors}
                    </Typography>
                    <Typography align="center">
                      Errors
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {promotionResult.data.errors && promotionResult.data.errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Errors occurred during promotion:
                </Typography>
                <ul>
                  {promotionResult.data.errors.slice(0, 3).map((error, index) => (
                    <li key={index}>{error.name}: {error.error}</li>
                  ))}
                  {promotionResult.data.errors.length > 3 && (
                    <li>... and {promotionResult.data.errors.length - 3} more</li>
                  )}
                </ul>
              </Alert>
            )}
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setPromotionDialog(false);
                setPromotionStep(0);
                setSelectedLearners([]);
                setRemarks("");
                setPromotionResult(null);
              }}
            >
              Done
            </Button>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Promotion Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Promote learners to next classes or graduate them to alumni
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Class for Promotion
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedStream("");
                }}
                label="Class"
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!selectedClass}>
              <InputLabel>Stream</InputLabel>
              <Select
                value={selectedStream}
                onChange={(e) => setSelectedStream(e.target.value)}
                label="Stream"
              >
                <MenuItem value="">All Streams</MenuItem>
                {selectedClass && schoolStreams[selectedClass]?.map((stream) => (
                  <MenuItem key={stream} value={stream}>{stream}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Academic Year"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
            />
          </Grid>
        </Grid>

        {selectedClass && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Learners in {selectedClass}
                {selectedStream && ` - ${selectedStream}`}
                {shouldGraduateToAlumni(selectedClass) && (
                  <Chip 
                    label="Graduating Class" 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchLearnersForClass}
                  disabled={loading}
                >
                  Refresh
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<ArrowUpward />}
                  onClick={() => {
                    if (learners.length === 0) {
                      alert("No learners found in this class");
                      return;
                    }
                    setPromotionDialog(true);
                  }}
                  disabled={loading || learners.length === 0}
                >
                  {shouldGraduateToAlumni(selectedClass) ? 'Graduate Class' : 'Promote Class'}
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : learners.length === 0 ? (
              <Alert severity="info">
                No learners found in {selectedClass}{selectedStream ? ` stream ${selectedStream}` : ''}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Admission No.</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Stream</TableCell>
                      <TableCell>Parent</TableCell>
                      <TableCell>Contact</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {learners.map((learner) => (
                      <TableRow key={learner._id} hover>
                        <TableCell>{learner.admissionNumber}</TableCell>
                        <TableCell>{learner.fullName}</TableCell>
                        <TableCell>{learner.stream || 'N/A'}</TableCell>
                        <TableCell>{learner.parentName}</TableCell>
                        <TableCell>{learner.parentContact}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Promotion Dialog */}
      <Dialog
        open={promotionDialog}
        onClose={() => {
          if (!promoting) {
            setPromotionDialog(false);
            setPromotionStep(0);
            setRemarks("");
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArrowUpward />
            {shouldGraduateToAlumni(selectedClass) ? 'Graduate Class' : 'Promote Class'}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={promotionStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Select Learners</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Results</StepLabel>
            </Step>
          </Stepper>
          
          {renderPromotionStep()}
        </DialogContent>
        
        <DialogActions>
          {promotionStep < 2 && (
            <>
              <Button
                onClick={() => {
                  if (promotionStep > 0) {
                    setPromotionStep(promotionStep - 1);
                  } else {
                    setPromotionDialog(false);
                    setPromotionStep(0);
                  }
                }}
                disabled={promoting}
              >
                {promotionStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              
              <Button
                onClick={() => {
                  if (promotionStep === 0) {
                    if (selectedLearners.length === 0) {
                      alert("Please select at least one learner");
                      return;
                    }
                    setPromotionStep(1);
                  } else if (promotionStep === 1) {
                    handlePromote();
                  }
                }}
                variant="contained"
                color="primary"
                disabled={promoting}
              >
                {promoting ? (
                  <CircularProgress size={24} />
                ) : promotionStep === 1 ? (
                  'Confirm Promotion'
                ) : (
                  'Next'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromotionManagement;